import React, { useState } from 'react';
import { useLayoutContext } from 'contexts/LayoutContext';
import { useParams } from 'react-router-dom';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import { OpenInNew } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  styled,
  Typography,
} from '@mui/material';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import { Column, Row } from 'components/Layout';
import FullPageDrawer from 'components/new/FullPageDrawer';
import ProcessingPage from 'components/new/ProcessingPage';

type BankBalanceAccount = {
  id: string;
  bankName: string;
  accountName: string;
  accountIdentifier: string | null;
  accountCurrency: string;
  calculatedBalance: number;
};

const AccountCard = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(theme.spacingSizes.sm),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(theme.spacingSizes.xs),
  width: '100%',
}));

const SmallAvatar = styled(Avatar)({
  width: 32,
  height: 32,
});

const BalanceSection = styled(Box)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
}));

const DottedUnderline = styled('span')({
  borderBottom: '1px dotted currentColor',
});

type AccountCardProps = {
  account: BankBalanceAccount;
};

const BankAccountCard: React.FC<AccountCardProps> = ({ account }) => (
  <AccountCard>
    <Row centered justify="space-between">
      <Row centered spacing="sm">
        <SmallAvatar variant="circular">{account.accountName[0]}</SmallAvatar>
        <Column>
          <Typography variant="h6">{account.accountName}</Typography>
          {account.accountIdentifier && (
            <Typography variant="body2" color="text.secondary">
              {account.accountIdentifier}
            </Typography>
          )}
        </Column>
      </Row>
      <IconButton size="small">
        <OpenInNew fontSize="small" />
      </IconButton>
    </Row>
    <Divider />
    <BalanceSection>
      <Column>
        <Typography variant="h6" color="text.secondary">
          Further-calculated balance
        </Typography>
        <Typography variant="h6">
          {numberToCurrencyString(account.calculatedBalance, {
            currency: account.accountCurrency,
          })}
        </Typography>
      </Column>
    </BalanceSection>
  </AccountCard>
);

type RouteParams = {
  periodId: string;
};

type Props = {
  onExit: () => void;
  periodEnd: CalendarDay;
};

const BankBalances: React.FC<Props> = ({ onExit, periodEnd }) => {
  const { vehicle } = useLayoutContext();
  const { periodId } = useParams<RouteParams>();

  const [isProcessing, setIsProcessing] = useState(false);

  const handleMutation = useHandleTrpcMutation<string>();
  const confirmChecklistItems =
    api.accountingPeriod.confirmChecklistItems.useMutation(
      handleMutation({ refetchType: 'active' }),
    );

  const handleMarkAsReviewed = async () => {
    if (!vehicle?._id || !periodId) return;
    setIsProcessing(true);
    try {
      await confirmChecklistItems.mutateAsync({
        vehicleId: vehicle._id,
        id: periodId,
        items: ['bankBalancesStatement'],
        confirmed: true,
      });
      onExit();
    } catch {
      setIsProcessing(false);
    }
  };

  const [rawAccounts] =
    api.bankAccount.listBankAccountsAtPeriodEnd.useSuspenseQuery({
      vehicleId: vehicle?._id ?? '',
      periodEnd,
    });

  const accounts: Array<BankBalanceAccount> = rawAccounts.map((a) => {
    const iban = a.iban?.trim();

    return {
      id: a.id,
      bankName: a.bankName,
      accountName: a.accountName,
      accountIdentifier: iban || a.accountNumber?.trim() || null,
      accountCurrency: a.accountCurrency,
      calculatedBalance: a.calculatedBalance,
    };
  });

  return (
    <FullPageDrawer
      open
      onClose={onExit}
      size="lg"
      title="End of quarter bank balances"
      actions={
        <Row fullWidth justify="space-between">
          <Button size="large" variant="outlined" onClick={onExit}>
            Exit
          </Button>
          <Button
            size="large"
            variant="contained"
            onClick={handleMarkAsReviewed}
            disabled={isProcessing}
          >
            Mark as reviewed
          </Button>
        </Row>
      }
    >
      {isProcessing ? (
        <ProcessingPage
          title="Marking as reviewed"
          subtitle="This will only take a moment."
        />
      ) : (
        <Column fullWidth spacing="lg">
          <Typography variant="h5">Review account balances</Typography>
          <Typography variant="body2">
            Verify that the{' '}
            <DottedUnderline>Further-calculated balance</DottedUnderline>{' '}
            matches your end of quarter bank balances as of{' '}
            {periodEnd.toReadable()}.
          </Typography>
          <Column spacing="lg">
            {accounts.map((account) => (
              <BankAccountCard key={account.id} account={account} />
            ))}
          </Column>
        </Column>
      )}
    </FullPageDrawer>
  );
};

export default BankBalances;
